import { useEffect, useState } from "react";
import { Zap, X, Loader2, Wallet as WalletIcon, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import LevelBadge from "@/components/LevelBadge";

const PRESETS = [
  { id: "tip_1",  cents: 100,  label: "$1",  desc: "spark" },
  { id: "tip_3",  cents: 300,  label: "$3",  desc: "pulse" },
  { id: "tip_5",  cents: 500,  label: "$5",  desc: "surge" },
  { id: "tip_10", cents: 1000, label: "$10", desc: "overdrive" },
];

export default function TipDialog({ open, onClose, video, onWalletTipSuccess, initialCustomAmount, initialMessage }) {
  const { user } = useAuth();
  const [selectedPreset, setSelectedPreset] = useState("tip_3");
  const [customAmount, setCustomAmount] = useState("");
  const [tipperName, setTipperName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [source, setSource] = useState("wallet"); // "wallet" | "card"
  const [wallet, setWallet] = useState(null);

  // Load wallet snapshot when opened (logged-in users only)
  useEffect(() => {
    if (!open || !user) return;
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => setWallet(null));
  }, [open, user]);

  // Prefill custom amount + message when opened with a preset (e.g. steal-crown)
  useEffect(() => {
    if (!open) return;
    if (initialCustomAmount != null && initialCustomAmount > 0) {
      setCustomAmount(String(initialCustomAmount));
    }
    if (initialMessage) {
      setMessage(initialMessage);
    }
  }, [open, initialCustomAmount, initialMessage]);

  // Guests can only use card
  useEffect(() => {
    if (!user && source === "wallet") setSource("card");
  }, [user, source]);

  if (!open) return null;

  const useCustom = customAmount.trim() !== "";
  const currentCents = useCustom
    ? Math.round((parseFloat(customAmount) || 0) * 100)
    : PRESETS.find((p) => p.id === selectedPreset)?.cents || 0;
  // Spendable = purchased wallet + earnings (both usable; earnings drawn first).
  const walletBalance = wallet?.wallet_balance_cents || 0;
  const earningsBalance = wallet?.earnings_cents || 0;
  const balance = walletBalance + earningsBalance;
  const canPayFromWallet = user && source === "wallet" && currentCents > 0 && currentCents <= balance;

  const handleBoost = async () => {
    if (submitting) return;
    if (!user && !tipperName.trim()) {
      toast.error("Enter a display name so the creator can see who boosted");
      return;
    }
    if (currentCents < 100) {
      toast.error("Minimum boost is $1.00");
      return;
    }
    if (currentCents > 50000) {
      toast.error("Custom amount must be under $500");
      return;
    }

    setSubmitting(true);

    // -------- Wallet-funded boost (instant, no Stripe) --------
    if (user && source === "wallet") {
      if (currentCents > balance) {
        toast.error("Not enough wallet balance — top up or switch to card");
        setSubmitting(false);
        return;
      }
      try {
        const res = await api.post(`/videos/${video.id}/tip/wallet`, {
          amount_cents: currentCents,
          message: message.trim() || undefined,
        });
        toast.success("Boost sent · gift delivered");
        // Trigger the sender-side takeover for all tiers ≥ $1
        if (currentCents >= 100) {
          window.dispatchEvent(
            new CustomEvent("neosync:gift-preview", {
              detail: {
                mode: "sent",
                amount_cents: currentCents,
                tipper_name: video.author?.name || `@${video.author?.username}` || "Creator",
                tipper_picture: video.author?.picture,
                message: message.trim() || null,
              },
            })
          );
        }
        onWalletTipSuccess?.(res.data);
        onClose?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Boost failed");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // -------- Stripe checkout path (guests + card mode) --------
    const payload = {
      origin_url: window.location.origin,
      tipper_name: user ? undefined : tipperName.trim(),
      message: message.trim() || undefined,
    };
    if (useCustom) {
      payload.custom_amount = parseFloat(customAmount);
    } else {
      payload.package_id = selectedPreset;
    }
    try {
      const res = await api.post(`/videos/${video.id}/tip/checkout`, payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="tip-dialog"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0A0A0C] border border-[#FF007F]/40 shadow-[0_0_60px_rgba(255,0,127,0.35)] relative max-h-[90vh] overflow-y-auto"
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0C] z-10">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#FF007F]" fill="#FF007F" strokeWidth={0} />
            <span
              className="font-display font-black text-lg tracking-tight glitch"
              data-text="BOOST"
              style={{ textShadow: "0 0 12px rgba(255,0,127,0.55)" }}
            >
              BOOST
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              // tip.creator
            </span>
          </div>
          <button
            data-testid="tip-dialog-close"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-white/80 text-sm">
            Send{" "}
            <span className="font-semibold text-white">
              {video.author?.name || `@${video.author?.username}`}
            </span>{" "}
            a boost — level up your gifter badge.
          </p>

          {/* Payment source tabs (auth users only) */}
          {user && (
            <div className="grid grid-cols-2 gap-2" data-testid="tip-source-tabs">
              <button
                data-testid="tip-source-wallet"
                onClick={() => setSource("wallet")}
                className={`py-3 flex flex-col items-center justify-center gap-0.5 border transition-all ${
                  source === "wallet"
                    ? "border-[#39FF14] bg-[#39FF14]/10 text-white shadow-[0_0_12px_rgba(57,255,20,0.3)]"
                    : "border-white/15 text-white/60 hover:border-white/40"
                }`}
              >
                <div className="flex items-center gap-2">
                  <WalletIcon className="h-4 w-4" strokeWidth={1.8} />
                  <span className="font-mono text-xs uppercase tracking-widest">balance</span>
                  <span className="font-display font-bold text-xs text-[#39FF14]">
                    ${(balance / 100).toFixed(2)}
                  </span>
                </div>
                {earningsBalance > 0 && (
                  <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    wallet ${(walletBalance / 100).toFixed(0)} · earnings ${(earningsBalance / 100).toFixed(0)}
                  </span>
                )}
              </button>
              <button
                data-testid="tip-source-card"
                onClick={() => setSource("card")}
                className={`py-3 flex items-center justify-center gap-2 border transition-all ${
                  source === "card"
                    ? "border-[#00FFFF] bg-[#00FFFF]/10 text-white shadow-[0_0_12px_rgba(0,255,255,0.3)]"
                    : "border-white/15 text-white/60 hover:border-white/40"
                }`}
              >
                <CreditCard className="h-4 w-4" strokeWidth={1.8} />
                <span className="font-mono text-xs uppercase tracking-widest">card</span>
              </button>
            </div>
          )}

          {/* Preset chips */}
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => {
              const active = !useCustom && selectedPreset === p.id;
              const disabled = user && source === "wallet" && p.cents > balance;
              return (
                <button
                  key={p.id}
                  data-testid={`tip-preset-${p.id}`}
                  disabled={disabled}
                  onClick={() => {
                    setSelectedPreset(p.id);
                    setCustomAmount("");
                  }}
                  className={`py-3 flex flex-col items-center border transition-all ${
                    disabled
                      ? "border-white/5 text-white/20 cursor-not-allowed"
                      : active
                      ? "border-[#FF007F] bg-[#FF007F]/10 text-white shadow-[0_0_18px_rgba(255,0,127,0.4)]"
                      : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                  }`}
                >
                  <span className="font-display font-black text-xl">{p.label}</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                    {p.desc}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 block">
              // or.custom.amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display font-bold text-white/40">
                $
              </span>
              <input
                data-testid="tip-custom-amount"
                type="number"
                min="1"
                max="500"
                step="0.5"
                inputMode="decimal"
                placeholder="0.00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#00FFFF]/60 focus:outline-none py-3 pl-8 pr-3 text-white font-display text-lg tracking-tight"
              />
            </div>
          </div>

          {/* Guest name */}
          {!user && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 block">
                // your.display.name
              </label>
              <input
                data-testid="tip-guest-name"
                type="text"
                maxLength={40}
                placeholder="cyber_ghost"
                value={tipperName}
                onChange={(e) => setTipperName(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#39FF14]/60 focus:outline-none py-3 px-3 text-white"
              />
            </div>
          )}

          {/* Message */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 block">
              // message.optional
            </label>
            <textarea
              data-testid="tip-message"
              rows={2}
              maxLength={280}
              placeholder="loved this loop"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-black border border-white/15 focus:border-[#00FFFF]/60 focus:outline-none py-2 px-3 text-white text-sm resize-none"
            />
          </div>

          {/* Gifter level preview */}
          {user && wallet?.gifter && (
            <div
              data-testid="tip-gifter-preview"
              className="flex items-center gap-3 border border-white/10 bg-black/50 p-3"
            >
              <LevelBadge level={wallet.gifter.level} kind="gifter" size="md" showLabel={false} />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  your.gifter.level
                </p>
                <p className="text-white text-sm font-semibold">
                  L{wallet.gifter.level} · lifetime ${(wallet.gifter.lifetime_cents / 100).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* Insufficient wallet CTA */}
          {user && source === "wallet" && currentCents > balance && (
            <div className="p-3 border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] text-xs flex items-center justify-between gap-3">
              <span className="font-mono uppercase tracking-widest">
                need ${((currentCents - balance) / 100).toFixed(2)} more
              </span>
              {wallet?.can_topup ? (
                <Link
                  to="/wallet"
                  data-testid="tip-topup-link"
                  onClick={onClose}
                  className="font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 border border-[#FFB800] hover:bg-[#FFB800]/10"
                >
                  Top up
                </Link>
              ) : (
                <span
                  data-testid="tip-topup-locked"
                  className="font-mono uppercase tracking-widest text-[10px] text-[#FFB800]/70"
                >
                  earn coins to boost
                </span>
              )}
            </div>
          )}

          <button
            data-testid="tip-submit-btn"
            onClick={handleBoost}
            disabled={submitting || (user && source === "wallet" && currentCents > balance)}
            className="w-full bg-[#FF007F] text-white font-bold uppercase tracking-widest py-4 hover:shadow-[0_0_30px_rgba(255,0,127,0.6)] transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {source === "wallet" ? "sending boost…" : "connecting to stripe…"}
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" fill="white" strokeWidth={0} />
                {source === "wallet" ? `boost · $${(currentCents / 100).toFixed(2)}` : "send boost"}
              </>
            )}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 text-center">
            {source === "wallet"
              ? "instant · deducted from your vault"
              : "secured by stripe · test mode"}
          </p>
        </div>
      </div>
    </div>
  );
}
